import React, { useState, useEffect } from "react";
import { Box, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Grid, Badge, IconButton, Tooltip, Menu, MenuItem, LinearProgress } from "@material-ui/core";
import { Star as StarIcon, Check as CheckIcon, Close as CloseIcon, MoreVert as MoreVertIcon } from "@material-ui/icons";
import Markdown from "react-markdown";
import { Booking, BookingParticipant, getBookingOptions, BookingOptions, getParticipationCost, getInvitationPayment, patchInvitation, patchCheckIn, postParticipationRequest, deleteParticipationRequest, patchParticipationRequest, getBookingsSingle } from "api/bookings";
import moment from "moment";
import { UserProfile } from "api/user";
import { useSnackbar } from "notistack";
import { formatter, markdownIfYoutubelink } from "utils/helpers";
import { Club } from "api/clubs";
import styles from "./BookingDetailDialog.module.scss";
import i18n from "../i18n";
import EditBookingDialog from "./EditBookingDialog";
import CancelBookingDialog from "./CancelBookingDialog";
import RequestCancellationDialog from "./RequestCancellationDialog";
import EditRootGroupsDialog from "./EditRootGroupsDialog";

// prop types
interface BookingDetailDialogProps {
    bookingId?: string,
    club: Club | null,
    show: boolean,
    userProfile: UserProfile | null,
    onClose: () => void,
    onCreateNewBooking: (booking: Booking) => void,
    onFetchBookings: (clubId: string, date: string) => void,
    getCompanyBookings: (clubId: string) => void,
}

// booking details dialog
const BookingDetailDialog = ({ bookingId, club, show, userProfile, onClose, onCreateNewBooking, onFetchBookings, getCompanyBookings }: BookingDetailDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [expectedPriceCents, setExpectedPriceCents] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [options, setOptions] = useState<BookingOptions>();
    const [booking, setBooking] = useState<Booking>();
    const [showCancelBookingDialog, setShowCancelBookingDialog] = useState(false);
    const [showEditBookingDialog, setShowEditBookingDialog] = useState(false);
    const [showEditRootGroupsDialog, setShowEditRootGroupsDialog] = useState(false);
    const [showMoreOptions, setShowMoreOptions] = useState<HTMLButtonElement | null>(null);
    const [showRequestCancellationDialog, setShowRequestCancellationDialog] = useState(false);

    const isStandardUser = userProfile?.memberships.find((m) => m.club.id === club?.id)?.userRole === "standard";
    const isRootUser = userProfile?.userTypes?.includes("root-management");
    const isCompanyExclusive = booking?.bookingTags.includes("company-exclusive");
    const isCompany = club?.capabilities.includes("company");

    // fetch options
    const fetchOptions = async (id: string) => setOptions(await getBookingOptions({ bookingId: id }));
    const fetchBooking = async (id: string) => {
        try {
            setLoading(true);
            setBooking(await getBookingsSingle({ bookingId: id }));
        } catch (error) {
            displayError(error);
        } finally {
            setLoading(false);
        }
    };
    const fetchPaymentInfo = async (id: string) => {
        if (options!.possibleOptions.includes("answer-invitation")) {
            const { priceCents } = await getInvitationPayment({ bookingId: id });
            setExpectedPriceCents(priceCents);
        } else if (options!.possibleOptions.includes("request-participation")) {
            const { priceCents } = await getParticipationCost({ bookingId: id });
            setExpectedPriceCents(priceCents);
        } else {
            setExpectedPriceCents(0);
        }
    };
    useEffect(() => {
        if (bookingId) {
            fetchBooking(bookingId);
            fetchOptions(bookingId);
        }
    }, [bookingId]);
    useEffect(() => { if (bookingId && options) fetchPaymentInfo(bookingId); }, [options]); // eslint-disable-line

    // answer booking invitation
    const answerBookingInvitation = async (accept: boolean) => {
        setLoading(true);
        try {
            await patchInvitation({
                accept,
                bookingId: booking!.id,
                clientLatitude: club?.latitude,
                clientLongitude: club?.longitude,
                expectedPriceCents,
            });
            enqueueSnackbar(i18n.answerInvitationSuccess(accept), { variant: "success" });
            onFetchBookings(booking!.court.clubId, booking!.startDate);
            if (!accept) onClose();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // perform check-in
    const performCheckIn = async () => {
        setLoading(true);
        try {
            await patchCheckIn({
                bookingId: booking!.id,
                clientLatitude: club!.latitude,
                clientLongitude: club!.longitude,
            });
            enqueueSnackbar(i18n.checkInSuccess, { variant: "success" });
            onFetchBookings(booking!.court.clubId, booking!.startDate);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // request participation
    const requestParticipation = async () => {
        setLoading(true);
        try {
            await postParticipationRequest({
                bookingId: booking!.id,
                expectedPriceCents,
            });
            enqueueSnackbar(i18n.participationRequestSuccess, { variant: "success" });
            onFetchBookings(booking!.court.clubId, booking!.startDate);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // cancel participation request
    const cancelParticipationRequest = async () => {
        setLoading(true);
        try {
            await deleteParticipationRequest({
                bookingParticipantId: booking!.participants.find((p) => p.userId === userProfile!.id)!.id,
            });
            enqueueSnackbar(i18n.cancelParticipationRequestSuccess, { variant: "success" });
            onFetchBookings(booking!.court.clubId, booking!.startDate);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // answer participation request
    const answerParticipationRequest = async (bookingParticipantId: string, accept: boolean) => {
        setLoading(true);
        try {
            await patchParticipationRequest({ accept, bookingParticipantId });
            enqueueSnackbar(i18n.answerRequestSuccess(accept), { variant: "success" });
            onFetchBookings(booking!.court.clubId, booking!.startDate);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // participant
    const Participant = ({ participant: p }: { participant: BookingParticipant }) => {
        // determine badge icon
        let badgeContent = null;
        let badgeColor: "primary" | "secondary" | "error" = "primary";
        if (p.participantRole === "coach") {
            badgeContent = <StarIcon fontSize="inherit" />;
        } else if (p.invitationStatus === "accepted") {
            badgeContent = <CheckIcon fontSize="inherit" />;
        } else if (p.invitationStatus === "pending") {
            badgeColor = "secondary";
            badgeContent = "?";
        } else if (p.invitationStatus === "requested-to-join") {
            badgeColor = options?.possibleOptions.includes("answer-participation-requests") ? "error" : "secondary";
            badgeContent = "?";
        } else if (p.invitationStatus === "declined" || p.invitationStatus === "request-declined") {
            badgeColor = "secondary";
            badgeContent = <CloseIcon fontSize="inherit" />;
        }

        // render
        return (
            <Box className={styles.participant}>
                <Badge
                    anchorOrigin={{
                        horizontal: "right",
                        vertical: "bottom",
                    }}
                    badgeContent={badgeContent}
                    classes={{ badge: styles.participantBadge }}
                    color={badgeColor}
                    overlap="circle"
                >
                    <Avatar src={p.user.profilePictureUrl} />
                </Badge>
                <Typography
                    style={{ marginTop: "4px" }}
                    variant="body2"
                >
                    {`${p.user.firstName} ${p.user.lastName}`}
                </Typography>

                {/* answer participation requests */}
                {options?.possibleOptions.includes("answer-participation-requests") && p.invitationStatus === "requested-to-join" && (
                    <Box style={{ display: "flex" }}>
                        <Tooltip title={i18n.acceptRequest}>
                            <IconButton onClick={() => answerParticipationRequest(p.id, true)}>
                                <CheckIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.declineRequest}>
                            <IconButton onClick={() => answerParticipationRequest(p.id, false)}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        );
    };

    // render
    return (
        <>
            {/* booking details dialog */}
            <Dialog
                fullWidth
                open={show && !showEditBookingDialog && !showCancelBookingDialog}
                onClose={onClose}
            >
                <DialogTitle>{booking?.description || i18n.bookingTypes[booking?.bookingType!]}</DialogTitle>
                <DialogContent dividers>
                    {/* quick info */}
                    {isLoading && <LinearProgress />}
                    {booking && (
                        <Box className={styles.quickInfo}>
                            <Typography variant="body2">
                                {i18n.bookingTypes[booking?.bookingType || ""]}
                                {" \u2022 "}
                                {moment(booking?.startDate).format("lll")}
                                {" \u2022 "}
                                {`${moment(booking?.endDate).diff(booking?.startDate, "minutes")} min`}
                                {" \u2022 "}
                                {booking?.court.courtName}
                            </Typography>
                            {booking?.finalizationDeadline && (
                                <Typography variant="body2" style={{ fontWeight: "bold" }}>
                                    {`${i18n.finalizationDeadline}: ${moment(booking.finalizationDeadline).format("lll")}`}
                                </Typography>
                            )}
                            {booking?.detailsDescription && (
                                <>
                                    <Typography>&nbsp;</Typography>
                                    <Markdown linkTarget="_blank">{booking?.detailsDescription}</Markdown>
                                </>
                            )}
                            {booking?.privateDescription && (
                                <>
                                    <Typography variant="body2" style={{ fontWeight: "bold" }}>{i18n.moreInformation}</Typography>
                                    <Markdown linkTarget="_blank">{markdownIfYoutubelink(booking?.privateDescription)}</Markdown>
                                </>
                            )}
                            {booking?.galleryMediaUrls && (booking?.galleryMediaUrls).length > 0 && (
                                <Box className={styles.logo}>
                                    <Avatar src={booking?.galleryMediaUrls[0]} className="club_avatar" />
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* participants */}
                    {/* <Typography variant="subtitle2">{i18n.participants}</Typography> */}
                    <Grid container spacing={2}>
                        {booking?.participants.map((p) => (
                            <Grid item xs={4} key={p.id}>
                                <Participant participant={p} />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {(options?.possibleOptions.includes("request-cancellation") || options?.possibleOptions.includes("edit-booking-groups") || !isCompany) && (
                        <IconButton size="small" style={{ marginRight: "auto" }} onClick={(e) => setShowMoreOptions(e.currentTarget)}>
                            <MoreVertIcon />
                        </IconButton>
                    )}
                    {options?.possibleOptions.includes("cancel") && (
                        <Button color="secondary" onClick={() => setShowCancelBookingDialog(true)}>
                            {i18n.cancelB}
                        </Button>
                    )}
                    {options?.possibleOptions.includes("edit") && (
                        <Button color="secondary" onClick={() => setShowEditBookingDialog(true)}>
                            {i18n.edit}
                        </Button>
                    )}
                    {options?.possibleOptions.includes("answer-invitation") && (
                        <>
                            <Button color="secondary" onClick={() => answerBookingInvitation(false)} disabled={isLoading}>
                                {i18n.decline}
                            </Button>
                            <Button color="secondary" onClick={() => answerBookingInvitation(true)} disabled={isLoading}>
                                {i18n.accept(expectedPriceCents ? formatter.format(expectedPriceCents / 100) : "")}
                            </Button>
                        </>
                    )}
                    {options?.possibleOptions.includes("check-in") && (
                        <Button color="secondary" onClick={performCheckIn} disabled={isLoading}>
                            {i18n.checkIn}
                        </Button>
                    )}
                    {options?.possibleOptions.includes("request-participation") && (
                        <Button color="secondary" onClick={requestParticipation} disabled={isLoading}>
                            {i18n.participate(expectedPriceCents ? formatter.format(expectedPriceCents / 100) : "")}
                        </Button>
                    )}
                    {options?.possibleOptions.includes("delete-participation-request") && (
                        <Button color="secondary" onClick={cancelParticipationRequest} disabled={isLoading}>
                            {i18n.cancelParticipationRequest}
                        </Button>
                    )}
                    <Button color="primary" onClick={onClose}>
                        {i18n.ok}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* edit booking dialog */}
            <EditBookingDialog
                club={club}
                booking={booking}
                options={options}
                show={showEditBookingDialog}
                onCancel={() => setShowEditBookingDialog(false)}
                onClose={() => { onClose(); setShowEditBookingDialog(false); }}
                onFetchBookings={() => onFetchBookings(booking!.court.clubId, booking!.startDate)}
                getCompanyBookings={getCompanyBookings}
                fetchBooking={fetchBooking}
            />

            {/* edit root groups dialog */}
            <EditRootGroupsDialog
                booking={booking}
                show={showEditRootGroupsDialog}
                onClose={() => setShowEditRootGroupsDialog(false)}
            />

            {/* cancel booking dialog */}
            <CancelBookingDialog
                club={club}
                booking={booking}
                show={showCancelBookingDialog}
                userId={userProfile?.id}
                userRole={userProfile?.memberships.find((m) => m.club.id === club?.id)?.userRole}
                onCancel={() => setShowCancelBookingDialog(false)}
                onClose={() => { onClose(); setShowCancelBookingDialog(false); }}
                onFetchBookings={() => onFetchBookings(booking!.court.clubId, booking!.startDate)}
                getCompanyBookings={getCompanyBookings}
            />

            {/* request cancellation dialog */}
            <RequestCancellationDialog
                booking={booking}
                show={showRequestCancellationDialog}
                onClose={() => setShowRequestCancellationDialog(false)}
                onFetchBookings={() => onFetchBookings(booking!.court.clubId, booking!.startDate)}
            />

            {/* more options */}
            <Menu
                anchorEl={showMoreOptions}
                anchorOrigin={{ vertical: "center", horizontal: "center" }}
                getContentAnchorEl={null}
                open={!!showMoreOptions}
                onClose={() => setShowMoreOptions(null)}
            >
                {options?.possibleOptions.includes("request-cancellation") && (
                    <MenuItem onClick={() => setShowRequestCancellationDialog(true)} disabled={isLoading}>
                        {i18n.requestCancellation}
                    </MenuItem>
                )}
                {options?.possibleOptions.includes("edit-booking-groups") && (
                    <MenuItem onClick={() => { setShowMoreOptions(null); setShowEditRootGroupsDialog(true); }}>
                        {i18n.editRootGroups}
                    </MenuItem>
                )}
                {((!isCompany && !isCompanyExclusive && !isStandardUser) || isRootUser) && (
                    <MenuItem onClick={async () => { setShowMoreOptions(null); onCreateNewBooking(await getBookingsSingle({ bookingId: booking!.id })); }}>{i18n.useAsTemplate}</MenuItem>
                )}
            </Menu>
        </>
    );
};

export default BookingDetailDialog;
